/* eslint-disable @typescript-eslint/no-unused-vars */
import { Injectable, OnDestroy } from '@angular/core';
import { ApplicationInsights, IEventTelemetry, IPageViewPerformanceTelemetry, IPageViewTelemetry, SeverityLevel } from '@microsoft/applicationinsights-web';
import { ICustomProperties } from '@microsoft/applicationinsights-core-js';
import { IAnalyticsProvider } from '@app/models/app-initialisers/IAnalyticsProvider';
import { Subscription } from 'rxjs';
import { ExceptionTelemetry } from '@app/models/app-initialisers/ExceptionTelemetry';
import { MetricTelemetry } from '@app/models/app-initialisers/MetricTelemetry';
import { PageViewTelemetry } from '@app/models/app-initialisers/PageViewTelemetry';
import { TraceTelemetry } from '@app/models/app-initialisers/TraceTelemetry';
import { ConfigurationService } from '@app/app-initialisers/configuration-service/configuration.service';
import { GoogleAnalyticsItem } from '@app/models/app-initialisers/google-analytics-item';

declare const gtag: Function;

@Injectable({
  providedIn: 'root'
})
export class InsightsService implements IAnalyticsProvider, OnDestroy {
  private appInsights: ApplicationInsights;
  private subscription: Subscription;

  private _gtagAttempts: number = 0;

  constructor(public configurationService: ConfigurationService) { }

  insightsInit() {
    this.subscription = this.configurationService.isTenantReady.subscribe((status: boolean) => {
      if (status) {
        this.appInsights = new ApplicationInsights({
          config: {
            accountId: this.configurationService.getTenantId(),
            enableAutoRouteTracking: true,
            instrumentationKey: this.configurationService.getInstrumentationKey(),
            loggingLevelTelemetry: 2
          }
        });

        this.appInsights.loadAppInsights();

        const cloudRoleName = this.configurationService.getTelemetryRoleName();

        this.appInsights.addTelemetryInitializer((envelope) => {
          if (cloudRoleName) {
            envelope.tags['ai.cloud.role'] = cloudRoleName;
          }
          envelope.data ??= {};
          envelope.data['app_version'] = this.configurationService.getConfiguration().app.version;
          envelope.data['hostname'] = this.getHostname();
        });

        this.waitForGtag();

        return Promise.resolve();
      }
    });

    return this.subscription;
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  public pageView(path: string, title: string): void {
    this.appInsights?.trackPageView(new PageViewTelemetry(path, title));
  }

  public trackException(exception: Error, isFatal: boolean): void {
    this.appInsights?.trackException(new ExceptionTelemetry(exception));
  }

  public trackTrace(message: string, severity: SeverityLevel, properties?: ICustomProperties): void {
    const _properties: ICustomProperties = properties ? properties : {};
    _properties['href'] = window.location.href;

    this.appInsights?.trackTrace(new TraceTelemetry(message, severity), _properties);
  }

  public setupUserId(userId: string) {
    this.appInsights?.setAuthenticatedUserContext(userId);
  }

  public trackPerformance(category: string, path: string, time: number, label?: string): void {
    this.appInsights?.trackMetric(new MetricTelemetry(path, time));
  }

  public trackViewProduct(productName: string, productId: string, category: string, price: number, currency: string): void {
    return;
  }

  trackViewDeals(dealId: string, dealName: string): void {
    return;
  }

  public trackViewBasket(value: number, items: GoogleAnalyticsItem[]): void {
    return;
  }

  public trackClickProduct(productName: string, productId: string, category: string, price: number, currency: string): void {
    return;
  }

  public trackAddToBasket(value: number, items: GoogleAnalyticsItem[]): void {
    return;
  }

  public trackRemoveFromBasket(value: number, items: GoogleAnalyticsItem[]): void {
    return;
  }

  public trackBeginCheckout(value: number, coupon: string, items: GoogleAnalyticsItem[]): void {
    this.appInsights?.trackEvent({ name: 'beginCheckout' }, { value, coupon, items });
  }

  public trackPurchaseAndClose(amount: number, orderId: string, voucherCode: string, items: GoogleAnalyticsItem[]): void {
    this.appInsights?.trackEvent({ name: 'purchase' }, { amount, orderId, voucherCode, items });
  }

  public trackEvent(event: IEventTelemetry) {
    this.appInsights?.trackEvent(event);
  }

  public trackPageView(data: IPageViewTelemetry) {
    this.appInsights?.trackPageView(data);
  }

  public trackPageViewPerformance(pageData: IPageViewPerformanceTelemetry) {
    this.appInsights?.trackPageViewPerformance(pageData);
  }

  public setUserId(userId: string) {
    this.appInsights?.setAuthenticatedUserContext(userId);
  }

  public clearUserId() {
    this.appInsights?.clearAuthenticatedUserContext();
  }

  private getHostname(): string {
    return window.location.hostname.replace('www.', '');
  }

  /**
   * Added logging to track down GA issues.
   * @param attempt
   */
  private waitForGtag(): void {
    if (this.configurationService.getEnvironment() === 'Development' || this.configurationService.getEnvironment() === 'Test') {
      return;
    }

    this._gtagAttempts += 1;
    this.appInsights?.trackEvent({ name: `Waiting for Gtag - attempt: ${this._gtagAttempts}` });

    if (this._gtagAttempts === 1) {
      this.appInsights?.trackEvent({ name: `Tag manager key: ${this.configurationService.getTagManagerKey()}` });
    }

    const gtmScript = document.querySelector('script[src*=\'googletagmanager\']');

    if (gtmScript) {
      this.appInsights.trackEvent({ name: `✅ GTM script found: ${gtmScript['src']}` });
    } else {
      this.appInsights?.trackEvent({ name: `gtmScript not found - attempt: ${this._gtagAttempts}` });
    }

    try {
      gtag('event', 'test_event');
      this.appInsights?.trackEvent({ name: 'Google tags are working' });
    } catch (e) {
      this.appInsights?.trackEvent({ name: 'Google tags are not working' }, { error: e });
      this.trackException(e, false);

      if (this._gtagAttempts < 10) {
        this.appInsights?.trackEvent({ name: 'Retrying Google tags check' });
        setTimeout(() => this.waitForGtag(), 500);
      } else {
        this.appInsights?.trackEvent({ name: 'Google tags are not working after multiple attempts' });
      }
    }
  }
}
